import React from 'react'
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select'
import { Field, WrappedFieldProps } from 'redux-form'
import { useRegisterTab } from '../Tabs/TabItem'
import { MaterialFieldProps } from './MaterialField'
import IconButton from '@mui/material/IconButton'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Box from '@mui/material/Box'
import { useFieldError } from './hooks/useHelperText'
import { FormHelperText, InputLabel } from '@mui/material'
import { inputBaseClasses } from '@mui/material/InputBase'
import mergeSx from '@carsys/ui/src/helpers/merge-sx'

const styles = {
  formControl: {
    display: 'flex',
    flexDirection: 'row'
  },
  textfield: {
    width: '100%',
    [`& .${inputBaseClasses.input}`]: {
      border: 'unset !important' // styling collision with semantic causes a double border
    }
  },
  lockable: {
    [`& .${inputBaseClasses.root}`]: {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0
    }
  }
}

export interface DropdownOption {
  key?: string | number
  value?: string | number | string[]
  text: string
  disabled?: boolean
}

interface IDropdown {
  options: DropdownOption[]
  lockable?: boolean
  customErrorText?: string
}

interface OnLockToggleProps {
  onLockToggle?: (locked: boolean) => void
}

export interface ReduxInputProps extends Omit<MaterialFieldProps<SelectProps>, 'onChange'>, Pick<SelectProps, 'onChange'> {}

interface DropdownProps {
  onDropDownChange?: (e: SelectChangeEvent<unknown>) => void
}

export const RenderDropdown = (props: SelectProps & IDropdown & OnLockToggleProps & WrappedFieldProps & DropdownProps) => {
  const {
    onDropDownChange,
    lockable,
    disabled,
    label,
    placeholder,
    input: { onChange, value, ...restInput },
    value: _ignoredValue,
    options,
    fullWidth,
    meta: { initial },
    onLockToggle,
    className,
    sx,
    multiple,
    required,
    customErrorText,
    size = 'small',
    ...custom
  } = props

  // todo: gms-11333 why is full width not working?
  const [locked, setLocked] = React.useState(lockable && !!initial && initial.length > 0)
  const { isError, errorText } = useFieldError(props.meta)
  const defaultEmpty = !multiple ? '' : []

  const handleOnClick = () => {
    setLocked(!locked)
    onLockToggle?.(!locked)
  }

  const handleOnChange = (e: SelectChangeEvent<unknown>) => {
    onChange(e)
    onDropDownChange && e.target && onDropDownChange(e)
  }

  const endAdornment = lockable ? (
    <IconButton
      disabled={disabled}
      onClick={handleOnClick}
      disableRipple
      sx={theme => ({
        display: 'inline-flex',
        padding: theme.spacing(0.5, 1),
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderLeft: 0,
        borderRadius: 1,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
      })}
      size='large'
    >
      {locked ? <LockIcon /> : <LockOpenIcon />}
    </IconButton>
  ) : undefined

  const id = custom.id ?? custom.name ?? restInput.name

  return (
    <FormControl size={size} fullWidth={fullWidth} className={className} sx={sx} required={required} error={isError}>
      <InputLabel variant='outlined' id={id} required={required}>
        {label}
      </InputLabel>
      <Box sx={lockable ? styles.formControl : undefined}>
        <Select
          variant='outlined'
          labelId={id}
          fullWidth={fullWidth}
          sx={mergeSx(styles.textfield, lockable ? styles.lockable : undefined)}
          label={label}
          placeholder={placeholder}
          error={isError}
          disabled={disabled || locked}
          onChange={handleOnChange}
          value={(multiple && Array.isArray(value)) || (!multiple && isValueInOptions(options, value)) ? value : defaultEmpty}
          multiple={multiple}
          required={required}
          {...restInput}
          key={restInput.key === null ? undefined : restInput.key! /** patch types inconsistency: null != undefined */}
          {...custom}
        >
          {options.map((option: DropdownOption) => (
            <MenuItem key={option.key} value={option.value} disabled={option.disabled}>
              {option.text}
            </MenuItem>
          ))}
        </Select>
        {endAdornment}
      </Box>
      {isError && !(customErrorText === '') && (
        <FormHelperText
          error
          sx={theme => ({
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5)
          })}
        >
          {customErrorText || errorText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

function isValueInOptions(options: DropdownOption[], value: string): boolean {
  return options.some(option => option.value === value)
}

export const ReduxInput: React.FC<ReduxInputProps & IDropdown> = props => {
  useRegisterTab(props.name)

  const { validate, name, placeholder, label, options, onChange, ...rest } = props
  const sanitizeValidate = !validate ? undefined : Array.isArray(validate) ? validate : [validate]
  return (
    <Field
      validate={sanitizeValidate}
      name={name}
      placeholder={placeholder}
      component={RenderDropdown}
      label={label}
      variant='outlined'
      options={options}
      onDropDownChange={onChange}
      props={{ ...rest, name }}
    />
  )
}

ReduxInput.displayName = 'ReduxInput'

export default React.memo(ReduxInput)
